import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  Chip,
  Stack,
  Avatar,
  Button,
  Paper,
} from '@mui/material';
import {
  Storage,
  Security,
  Speed,
  Public,
  CheckCircle,
  ArrowForward,
  Router,
  CellTower,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import CTASection from '../components/common/CTASection';

const DataCenters = () => {
  const theme = useTheme();
  const navigate = useNavigate();

  // Consistent stats matching the correct data
  const stats = [
    { number: '50,000+', label: 'KM of Fiber Network', icon: <Router />, color: '#e60108' },
    { number: '100+', label: 'Telecom Towers', icon: <CellTower />, color: '#191919' },
    { number: '15+', label: 'Countries Served', icon: <Public />, color: '#e60108' },
    { number: '100%', label: 'Uptime Guarantee', icon: <CheckCircle />, color: '#191919' },
  ];

  const features = [
    { icon: <Security />, title: 'Enterprise Security', desc: '24/7 surveillance, biometric access, and security guards' },
    { icon: <Speed />, title: 'High Performance', desc: '100Gbps connectivity with 99.999% uptime SLA' },
    { icon: <Public />, title: 'Global Reach', desc: 'Connected to major cloud providers and networks' },
    { icon: <Storage />, title: 'Scalable Solutions', desc: 'From single rack to full private suites' },
  ];

  const locations = [
    { city: 'New York', country: 'USA', capacity: '15MW', tier: 'Tier IV', status: 'Operational' },
    { city: 'London', country: 'UK', capacity: '12MW', tier: 'Tier III+', status: 'Operational' },
    { city: 'Singapore', country: 'Singapore', capacity: '20MW', tier: 'Tier IV', status: 'Operational' },
    { city: 'Frankfurt', country: 'Germany', capacity: '10MW', tier: 'Tier III+', status: 'Operational' },
    { city: 'Tokyo', country: 'Japan', capacity: '8MW', tier: 'Tier III', status: 'Coming Soon' },
    { city: 'São Paulo', country: 'Brazil', capacity: '6MW', tier: 'Tier III', status: 'Coming Soon' },
  ];

  return (
    <Box>
      <PageHeader 
        title="Data Centers" 
        subtitle="Secure, scalable, and highly available infrastructure"
      />

      {/* Stats Section with Future Goal Note */}
      <Box sx={{ py: 6, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <Grid container spacing={3} justifyContent="center">
            {stats.map((stat, index) => (
              <Grid item xs={6} sm={3} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card sx={{ p: 3, textAlign: 'center', borderRadius: 3 }}>
                    <Avatar sx={{ bgcolor: stat.color + '15', color: stat.color, margin: '0 auto 16px', width: 60, height: 60 }}>
                      {stat.icon}
                    </Avatar>
                    <Typography variant="h3" fontWeight="bold" sx={{ color: stat.color, fontSize: { xs: '1.5rem', md: '2rem' } }}>
                      {stat.number}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                      {stat.label}
                    </Typography>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          {/* Single Future Goal Note */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            <Paper
              elevation={0}
              sx={{
                mt: 5,
                p: 3,
                bgcolor: '#f8f8f8',
                borderRadius: 3,
                textAlign: 'center',
                maxWidth: 700,
                mx: 'auto',
                border: '1px solid #e6010820',
              }}
            >
              <Typography variant="body1" sx={{ color: '#191919' }}>
                <Box component="span" sx={{ color: '#e60108', fontWeight: 700 }}>🎯 Future Goal:</Box> These statistics represent our ambitious targets for the coming years as we continue to expand and enhance our global communication infrastructure.
              </Typography>
            </Paper>
          </motion.div>
        </Container>
      </Box>

      {/* Features */}
      <Box sx={{ py: 8, bgcolor: '#f8f8f8' }}>
        <Container maxWidth="xl">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Chip 
                  label="Data Center Solutions" 
                  sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600 }}
                />
                <Typography variant="h2" sx={{ mb: 3, fontWeight: 800, color: '#191919' }}>
                  Enterprise-Grade Data Center Solutions
                </Typography>
                <Typography variant="body1" sx={{ color: '#a0a0a0', mb: 3, lineHeight: 1.8 }}>
                  Our data centers are designed to meet the most demanding requirements 
                  for security, reliability, and performance.
                </Typography>
                <Stack spacing={2}>
                  {features.map((feature, index) => (
                    <Stack direction="row" spacing={2} alignItems="center" key={index}>
                      <Avatar sx={{ bgcolor: '#e6010815', color: '#e60108' }}>{feature.icon}</Avatar>
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#191919' }}>
                          {feature.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#a0a0a0' }}>
                          {feature.desc}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
                <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                  <Button
                    variant="contained"
                    size="large"
                    endIcon={<ArrowForward />}
                    onClick={() => navigate('/contact')}
                    sx={{ mt: 4, bgcolor: '#e60108', '&:hover': { bgcolor: '#b80006' } }}
                  >
                    Request a Quote
                  </Button>
                </motion.div>
              </motion.div>
            </Grid>
            <Grid item xs={12} md={6}>
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=600&h=400&fit=crop"
                  alt="Data Center"
                  sx={{
                    width: '100%',
                    borderRadius: 4,
                    boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                  }}
                />
              </motion.div>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Locations */}
      <Box sx={{ py: 8, bgcolor: '#fefefe' }}>
        <Container maxWidth="xl">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Chip
              label="Global Locations"
              sx={{ mb: 2, bgcolor: '#e6010810', color: '#e60108', fontWeight: 600, mx: 'auto', display: 'table' }}
            />
            <Typography variant="h2" align="center" sx={{ mb: 2, fontWeight: 800, color: '#191919' }}>
              Global Locations
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: '#a0a0a0', mb: 6 }}>
              Strategically positioned data centers worldwide
            </Typography>
          </motion.div>

          <Grid container spacing={3} justifyContent="center">
            {locations.map((location, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -5 }}
                >
                  <Card sx={{ p: 3, borderRadius: 3 }}>
                    <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ color: '#191919' }}>
                      {location.city}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#a0a0a0' }} gutterBottom>
                      {location.country}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 2 }} flexWrap="wrap" useFlexGap>
                      <Chip 
                        label={`Capacity: ${location.capacity}`} 
                        size="small"
                        sx={{ bgcolor: '#e6010810', color: '#e60108' }}
                      />
                      <Chip 
                        label={`Tier: ${location.tier}`} 
                        size="small"
                        sx={{ bgcolor: '#19191910', color: '#191919' }}
                      />
                      <Chip 
                        label={location.status} 
                        size="small"
                        sx={{ 
                          bgcolor: location.status === 'Operational' ? '#00C85320' : '#FFC10720',
                          color: location.status === 'Operational' ? '#00C853' : '#FFC107',
                          fontWeight: 600,
                        }}
                      />
                    </Stack>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <CTASection 
        title="Secure Your Infrastructure" 
        subtitle="Get premium data center space with carrier-neutral connectivity"
        buttonText="Explore Locations"
        buttonLink="/contact"
      />
    </Box>
  );
};

export default DataCenters;